import { supabase } from '../lib/supabase';

// Convert database snake_case to React camelCase
const convertLeadConfigFromDb = (dbConfig) => {
  if (!dbConfig) return null;
  
  return {
    id: dbConfig?.id,
    userId: dbConfig?.user_id,
    configName: dbConfig?.config_name,
    currency: dbConfig?.currency,
    isActive: dbConfig?.is_active,
    monthlyLeads: dbConfig?.monthly_leads,
    conversionRatePercentage: dbConfig?.conversion_rate_percentage,
    averageDealValue: dbConfig?.average_deal_value,
    salesCycleDays: dbConfig?.sales_cycle_days,
    customerLifetimeValue: dbConfig?.customer_lifetime_value,
    totalMarketingBudget: dbConfig?.total_marketing_budget,
    totalMonthlyWonDeals: dbConfig?.total_monthly_won_deals,
    desiredMonthlyLeads: dbConfig?.desired_monthly_leads,
    desiredConversionRatePercentage: dbConfig?.desired_conversion_rate_percentage,
    profitMarginPercentage: dbConfig?.profit_margin_percentage,
    overheadPercentage: dbConfig?.overhead_percentage,
    marketingManagementType: dbConfig?.marketing_management_type,
    ppcBudget: dbConfig?.ppc_budget,
    agencyRetainerFee: dbConfig?.agency_retainer_fee,
    additionalBusinessCost: dbConfig?.additional_business_cost,
    createdAt: dbConfig?.created_at,
    updatedAt: dbConfig?.updated_at
  };
};

// Convert React camelCase to database snake_case
const convertLeadConfigToDb = (config) => {
  return {
    config_name: config?.configName,
    currency: config?.currency,
    is_active: config?.isActive,
    monthly_leads: config?.monthlyLeads,
    conversion_rate_percentage: config?.conversionRatePercentage,
    average_deal_value: config?.averageDealValue,
    sales_cycle_days: config?.salesCycleDays,
    customer_lifetime_value: config?.customerLifetimeValue,
    total_marketing_budget: config?.totalMarketingBudget,
    total_monthly_won_deals: config?.totalMonthlyWonDeals,
    desired_monthly_leads: config?.desiredMonthlyLeads,
    desired_conversion_rate_percentage: config?.desiredConversionRatePercentage,
    profit_margin_percentage: config?.profitMarginPercentage,
    overhead_percentage: config?.overheadPercentage,
    marketing_management_type: config?.marketingManagementType,
    ppc_budget: config?.ppcBudget,
    agency_retainer_fee: config?.agencyRetainerFee,
    additional_business_cost: config?.additionalBusinessCost
  };
};

// Convert lead source from DB
const convertLeadSourceFromDb = (dbSource) => {
  if (!dbSource) return null;
  
  return {
    id: dbSource?.id,
    configId: dbSource?.config_id,
    sourceType: dbSource?.source_type,
    monthlyCost: dbSource?.monthly_cost,
    monthlyLeads: dbSource?.monthly_leads,
    conversionRatePercentage: dbSource?.conversion_rate_percentage,
    isActive: dbSource?.is_active,
    createdAt: dbSource?.created_at,
    updatedAt: dbSource?.updated_at
  };
};

// Convert lead source to DB
const convertLeadSourceToDb = (source) => {
  return {
    config_id: source?.configId,
    source_type: source?.sourceType,
    monthly_cost: source?.monthlyCost,
    monthly_leads: source?.monthlyLeads,
    conversion_rate_percentage: source?.conversionRatePercentage,
    is_active: source?.isActive
  };
};

// CRITICAL FIX: Get effective user ID for all queries
const getEffectiveUserId = () => {
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    const { impersonatedUserId } = JSON.parse(impersonationData);
    return impersonatedUserId;
  }
  return null;
};

// Add this helper function
const toCamelCaseArray = (data) => {
  if (!data) return [];
  return data?.map(convertLeadConfigFromDb);
};

export const leadRoiCalculatorService = {
  // Get all configurations for current user
  async getAllConfigs() {
    try {
      const effectiveUserId = getEffectiveUserId();
      
      let query = supabase
        ?.from('lead_roi_calculator_configs')
        ?.select(`
          *,
          lead_sources(*),
          lead_quality_metrics(*)
        `)
        ?.order('created_at', { ascending: false });
      
      if (effectiveUserId) {
        query = query?.eq('user_id', effectiveUserId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching lead ROI configs:', error);
      throw error;
    }
  },

  // Alias for getAllConfigs for backwards compatibility
  async getConfigs() {
    return this.getAllConfigs();
  },

  // Get single configuration
  async getConfigById(configId) {
    const { data, error } = await supabase?.from('lead_roi_calculator_configs')?.select('*')?.eq('id', configId)?.single();

    if (error) throw error;
    return convertLeadConfigFromDb(data);
  },

  // Create new configuration
  async createConfig(config) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbConfig = {
      ...convertLeadConfigToDb(config),
      user_id: user?.id
    };

    const { data, error } = await supabase?.from('lead_roi_calculator_configs')?.insert(dbConfig)?.select()?.single();

    if (error) throw error;
    return convertLeadConfigFromDb(data);
  },

  // Update configuration
  async updateConfig(configId, updates) {
    const dbUpdates = convertLeadConfigToDb(updates);

    const { data, error } = await supabase?.from('lead_roi_calculator_configs')?.update(dbUpdates)?.eq('id', configId)?.select()?.single();

    if (error) throw error;
    return convertLeadConfigFromDb(data);
  },

  // Delete configuration
  async deleteConfig(configId) {
    const { error } = await supabase?.from('lead_roi_calculator_configs')?.delete()?.eq('id', configId);

    if (error) throw error;
    return true;
  },

  // Lead Sources Management
  async getLeadSources(configId) {
    const { data, error } = await supabase?.from('lead_sources')?.select('*')?.eq('config_id', configId)?.order('created_at', { ascending: true });

    if (error) throw error;
    return data?.map(convertLeadSourceFromDb) || [];
  },

  async createLeadSource(source) {
    const dbSource = convertLeadSourceToDb(source);

    const { data, error } = await supabase?.from('lead_sources')?.insert(dbSource)?.select()?.single();

    if (error) throw error;
    return convertLeadSourceFromDb(data);
  },

  async updateLeadSource(sourceId, updates) {
    const dbUpdates = convertLeadSourceToDb(updates);

    const { data, error } = await supabase?.from('lead_sources')?.update(dbUpdates)?.eq('id', sourceId)?.select()?.single();

    if (error) throw error;
    return convertLeadSourceFromDb(data);
  },

  async deleteLeadSource(sourceId) {
    const { error } = await supabase?.from('lead_sources')?.delete()?.eq('id', sourceId);

    if (error) throw error;
    return true;
  }
};